import React from 'react';
import { Box, Divider, Link, Typography } from '@mui/material';

/**
 * Rendu markdown minimal pour les réponses de l'assistant : gras, italique, code,
 * liens, listes et paragraphes. Volontairement sans dépendance externe, pour ne
 * pas alourdir le bundle de l'app apprenante, et sans `dangerouslySetInnerHTML`,
 * puisque le texte rendu provient d'un modèle de langage.
 */

const INLINE_PATTERN = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;

const renderInline = (text: string, keyPrefix: string): React.ReactNode[] =>
  text
    .split(INLINE_PATTERN)
    .filter((part) => part !== '' && part !== undefined)
    .map((part, index) => {
      const key = `${keyPrefix}-${index}`;

      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={key}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('*') && part.endsWith('*')) {
        return <em key={key}>{part.slice(1, -1)}</em>;
      }
      if (part.startsWith('`') && part.endsWith('`')) {
        return (
          <Box
            key={key}
            component="code"
            sx={{
              px: 0.6,
              py: 0.2,
              borderRadius: 1,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              fontFamily: 'monospace',
              fontSize: '0.85em',
            }}
          >
            {part.slice(1, -1)}
          </Box>
        );
      }

      const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (link) {
        return (
          <Link
            key={key}
            href={link[2]}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: 'var(--brand-primary)', textUnderlineOffset: 3 }}
          >
            {link[1]}
          </Link>
        );
      }

      return <React.Fragment key={key}>{part}</React.Fragment>;
    });

const isBulletLine = (line: string) => /^\s*[-*•]\s+/.test(line);
const isNumberedLine = (line: string) => /^\s*\d+[.)]\s+/.test(line);
const isHeadingLine = (line: string) => /^#{1,6}\s+/.test(line);
const isRuleLine = (line: string) => /^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line);

type RunKind = 'paragraph' | 'bullets' | 'numbers' | 'heading' | 'rule';

interface Run {
  kind: RunKind;
  lines: string[];
}

const lineKind = (line: string): RunKind => {
  if (isRuleLine(line)) return 'rule';
  if (isHeadingLine(line)) return 'heading';
  if (isBulletLine(line)) return 'bullets';
  if (isNumberedLine(line)) return 'numbers';
  return 'paragraph';
};

/**
 * Regroupe les lignes consécutives de même nature. Sans cela, une liste que le
 * modèle colle à sa phrase d'introduction, sans ligne vide, s'afficherait en
 * texte brut avec ses tirets apparents.
 */
const groupLines = (lines: string[]): Run[] =>
  lines.reduce<Run[]>((runs, line) => {
    const kind = lineKind(line);
    const previous = runs[runs.length - 1];

    if (previous && previous.kind === kind && kind !== 'heading' && kind !== 'rule') {
      previous.lines.push(line);
    } else {
      runs.push({ kind, lines: [line] });
    }

    return runs;
  }, []);

const listSx = { m: 0, mb: 1.25, pl: 2.5, '&:last-child': { mb: 0 } } as const;
const listItemSx = { mb: 0.5, lineHeight: 1.6 } as const;

const renderRun = (run: Run, key: string): React.ReactNode => {
  switch (run.kind) {
    case 'rule':
      return (
        <Divider key={key} sx={{ my: 1.5, borderColor: 'rgba(255, 255, 255, 0.12)' }} />
      );

    case 'heading':
      return (
        <Typography key={key} variant="subtitle2" sx={{ fontWeight: 700, mt: 0.5, mb: 0.5 }}>
          {renderInline(run.lines[0].replace(/^#{1,6}\s+/, ''), key)}
        </Typography>
      );

    case 'bullets':
    case 'numbers': {
      const isBullets = run.kind === 'bullets';
      const marker = isBullets ? /^\s*[-*•]\s+/ : /^\s*\d+[.)]\s+/;

      return (
        <Box key={key} component={isBullets ? 'ul' : 'ol'} sx={listSx}>
          {run.lines.map((line, i) => (
            <Typography key={`${key}-${i}`} component="li" variant="body2" sx={listItemSx}>
              {renderInline(line.replace(marker, ''), `${key}-${i}`)}
            </Typography>
          ))}
        </Box>
      );
    }

    default:
      return (
        <Typography
          key={key}
          variant="body2"
          sx={{ mb: 1.25, lineHeight: 1.65, whiteSpace: 'pre-wrap', '&:last-child': { mb: 0 } }}
        >
          {renderInline(run.lines.join('\n'), key)}
        </Typography>
      );
  }
};

const AssistantMarkdown: React.FC<{ text: string }> = ({ text }) => {
  // Les lignes vides séparent les paragraphes : on découpe d'abord dessus pour
  // ne pas fusionner deux paragraphes distincts en un seul bloc de texte.
  const blocks = text.trim().split(/\n{2,}/);

  return (
    <>
      {blocks.map((block, blockIndex) => {
        const lines = block.split('\n').filter((line) => line.trim());
        return groupLines(lines).map((run, runIndex) =>
          renderRun(run, `block-${blockIndex}-run-${runIndex}`)
        );
      })}
    </>
  );
};

export default AssistantMarkdown;
